import { z } from "zod";

/**
 * Importation et lecture des CV et pièces (devis, modules « Import et lecture
 * des CV » et « Reprise du papier »). Tout se passe sur le serveur :
 * texte (Poppler / Tesseract) → obfuscation (moteur Anonymum) → extraction des
 * champs par un modèle de langage LOCAL (Ollama) sur le texte obfusqué →
 * proposition à valider par une personne avant tout enregistrement.
 */
export const JOB_STATUSES = ["en_attente", "en_cours", "a_valider", "applique", "rejete", "echec"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  en_attente: "En attente",
  en_cours: "Lecture en cours",
  a_valider: "À valider",
  applique: "Appliqué",
  rejete: "Rejeté",
  echec: "Échec",
};

export const JOB_STEPS = ["texte", "obfuscation", "extraction", "proposition"] as const;
export type JobStep = (typeof JOB_STEPS)[number];

/** Champs que la lecture d'un CV peut proposer pour une fiche candidat. */
export const proposalFieldsSchema = z.object({
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  sex: z.enum(["F", "M", "X"]).nullable().optional(),
  birthDate: z.string().nullable().optional(),
  nationality: z.string().nullable().optional(),
  countryResidence: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  profession: z.string().nullable().optional(),
  educationLevel: z
    .enum(["aucun", "primaire", "secondaire", "professionnel", "collegial", "universitaire_1", "universitaire_2", "universitaire_3", "autre"])
    .nullable()
    .optional(),
  educationDetail: z.string().nullable().optional(),
  experienceYears: z.number().int().min(0).max(60).nullable().optional(),
  languages: z.array(z.string()).optional(),
  passportNumber: z.string().nullable().optional(),
  passportExpiresAt: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
});
export type ProposalFields = z.infer<typeof proposalFieldsSchema>;

/** Ce que l'utilisateur valide : les champs retenus (sous-ensemble corrigé de la proposition). */
export const applyProposalSchema = z.object({
  fields: proposalFieldsSchema,
  /** Dossier cible ; absent = créer un nouveau dossier à partir de ces champs. */
  candidateId: z.string().min(1).optional().nullable(),
});
export type ApplyProposalInput = z.infer<typeof applyProposalSchema>;

export interface ExtractionJob {
  id: string;
  documentId: string;
  candidateId: string | null;
  kind: "cv" | "piece";
  status: JobStatus;
  step: JobStep | null;
  error: string | null;
  textLength: number | null;
  model: string | null;
  /** Source de chaque champ proposé : « identite » (détection déterministe) ou « modele ». */
  proposal: { fields: ProposalFields; sources: Record<string, "identite" | "modele">; warnings: string[] } | null;
  originalName: string | null;
  createdBy: string | null;
  createdAt: number | string;
  startedAt: number | string | null;
  finishedAt: number | string | null;
  reviewedBy: string | null;
  reviewedAt: number | string | null;
}
